import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../service/core/api.service';

interface ActivityType {
    id?: string;
    name: string;
    icon?: string | null;
    sortOrder?: number;
    score?: number;
    isActive?: boolean;
}

/**
 * Admin CRUD for the configurable activity-type picklist (Settings → Activity Types).
 * These are the categories an agent picks when logging an activity on a case
 * (Call, Meeting, Site visit, …). Backed by /api/activity-types. A default set is
 * auto-seeded server-side, so the list is never empty.
 */
@Component({
    selector: 'app-activity-types',
    standalone: true,
    imports: [
        CommonModule, FormsModule, ButtonModule, TableModule, TagModule,
        InputTextModule, InputNumberModule, CheckboxModule, DialogModule, TooltipModule
    ],
    template: `
        <div class="at-head">
            <div>
                <h3 class="at-title">Activity Types</h3>
                <p class="at-sub">The categories your team picks when logging an activity on a case
                    (e.g. Call, Site visit). Inactive types stay on past activities but no longer appear in the picker.</p>
            </div>
            <button pButton icon="pi pi-plus" label="Add type" (click)="openNew()"></button>
        </div>

        <div class="at-panel">
            <p-table [value]="rows" [loading]="loading" dataKey="id" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:44%">Name</th>
                        <th style="width:12%">Order</th>
                        <th style="width:14%">Score</th>
                        <th style="width:14%">Status</th>
                        <th style="width:16%" class="at-actions-col">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-r>
                    <tr>
                        <td>{{ r.name }}</td>
                        <td>{{ r.sortOrder }}</td>
                        <td><span class="at-score">{{ r.score ?? 0 }}</span></td>
                        <td>
                            <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
                                   [severity]="r.isActive ? 'success' : 'secondary'"></p-tag>
                        </td>
                        <td class="at-actions-col">
                            <button pButton icon="pi pi-pencil" [text]="true" [rounded]="true" severity="secondary"
                                    pTooltip="Edit" (click)="openEdit(r)"></button>
                            <button pButton [icon]="r.isActive ? 'pi pi-trash' : 'pi pi-replay'"
                                    [text]="true" [rounded]="true" [severity]="r.isActive ? 'danger' : 'success'"
                                    [pTooltip]="r.isActive ? 'Deactivate' : 'Reactivate'"
                                    (click)="r.isActive ? remove(r) : reactivate(r)"></button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="5" class="at-empty">No activity types yet — add one to get started.</td></tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Add / edit dialog -->
        <p-dialog [(visible)]="showDialog" [modal]="true" [draggable]="false" [resizable]="false"
                  [style]="{ width: '420px' }" [header]="editing ? 'Edit activity type' : 'Add activity type'">
            <div class="at-form">
                <div class="at-field">
                    <label>Name <span class="at-req">*</span></label>
                    <input pInputText [(ngModel)]="draft.name" maxlength="120" placeholder="e.g. Site visit" />
                </div>
                <div class="at-field">
                    <label>Display order</label>
                    <p-inputNumber [(ngModel)]="draft.sortOrder" [min]="0" [showButtons]="true"></p-inputNumber>
                </div>
                <div class="at-field">
                    <label>Score <span class="at-hint">points added to the lead score each time this activity is logged (can be negative)</span></label>
                    <p-inputNumber [(ngModel)]="draft.score" [showButtons]="true"></p-inputNumber>
                </div>
                <div class="at-field at-field--row">
                    <p-checkbox [(ngModel)]="draft.isActive" [binary]="true" inputId="at-active"></p-checkbox>
                    <label for="at-active">Active (shown in the picker)</label>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <button pButton label="Cancel" text severity="secondary" (click)="showDialog = false" [disabled]="saving"></button>
                <button pButton icon="pi pi-check" label="Save" [loading]="saving"
                        [disabled]="saving || !draft.name?.trim()" (click)="save()"></button>
            </ng-template>
        </p-dialog>
    `,
    styles: [`
        .at-head { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; margin-bottom:1rem; }
        .at-title { margin:0; font-size:var(--gh-fs-16); font-weight:700; color:var(--text-color); }
        .at-sub { margin:.25rem 0 0; font-size:var(--gh-fs-13); color:var(--text-color-secondary); max-width:560px; line-height:1.45; }
        .at-panel { border:1px solid var(--surface-border); border-radius:var(--gh-r-md); overflow:hidden; }
        .at-actions-col { text-align:right; white-space:nowrap; }
        .at-empty { text-align:center; color:var(--text-color-secondary); padding:1.25rem; }
        .at-form { display:flex; flex-direction:column; gap:1rem; padding-top:.25rem; }
        .at-field { display:flex; flex-direction:column; gap:.35rem; }
        .at-field label { font-size:var(--gh-fs-13); font-weight:600; color:var(--text-color); }
        .at-hint { font-weight:400; color:var(--text-color-secondary); font-size:var(--gh-fs-12); margin-left:.35rem; }
        .at-score { display:inline-block; min-width:1.6rem; text-align:center; font-weight:700; font-size:var(--gh-fs-13); padding:.1rem .45rem; border-radius:var(--gh-r-pill); background:var(--primary-50, var(--gh-primary-wash)); color:var(--primary-color, var(--gh-primary)); }
        .at-field--row { flex-direction:row; align-items:center; gap:.5rem; }
        .at-field--row label { font-weight:500; }
        .at-req { color:var(--gh-danger); }
    `]
})
export class ActivityTypesComponent implements OnInit {

    rows: ActivityType[] = [];
    loading = false;
    showDialog = false;
    saving = false;
    editing: ActivityType | null = null;
    draft: ActivityType = { name: '', sortOrder: 0, isActive: true };

    constructor(private api: ApiService, private toast: MessageService) {}

    ngOnInit(): void { this.load(); }

    load(): void {
        this.loading = true;
        this.api.get<any>('/api/activity-types').subscribe({
            next: (res) => {
                const data = res?.data ?? res ?? [];
                this.rows = (Array.isArray(data) ? data : []) as ActivityType[];
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toast.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load activity types.' });
            }
        });
    }

    openNew(): void {
        this.editing = null;
        const nextOrder = this.rows.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), 0) + 1;
        this.draft = { name: '', sortOrder: nextOrder, score: 0, isActive: true };
        this.showDialog = true;
    }

    openEdit(r: ActivityType): void {
        this.editing = r;
        this.draft = { name: r.name, sortOrder: r.sortOrder ?? 0, score: r.score ?? 0, isActive: r.isActive !== false };
        this.showDialog = true;
    }

    save(): void {
        const name = (this.draft.name || '').trim();
        if (!name) return;
        this.saving = true;
        const body = { name, sortOrder: this.draft.sortOrder ?? 0, score: this.draft.score ?? 0, isActive: this.draft.isActive !== false };
        const req = this.editing?.id
            ? this.api.put<any>(`/api/activity-types/${this.editing.id}`, body)
            : this.api.post<any>('/api/activity-types', body);
        req.subscribe({
            next: () => {
                this.saving = false;
                this.showDialog = false;
                this.toast.add({ severity: 'success', summary: 'Saved', detail: this.editing ? 'Activity type updated.' : 'Activity type added.' });
                this.load();
            },
            error: () => {
                this.saving = false;
                this.toast.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save the activity type.' });
            }
        });
    }

    remove(r: ActivityType): void {
        if (!r.id) return;
        this.api.delete<any>(`/api/activity-types/${r.id}`).subscribe({
            next: () => {
                this.toast.add({ severity: 'success', summary: 'Deactivated', detail: 'Activity type hidden from the picker.' });
                this.load();
            },
            error: () => this.toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not deactivate the activity type.' })
        });
    }

    reactivate(r: ActivityType): void {
        if (!r.id) return;
        this.api.put<any>(`/api/activity-types/${r.id}`, { isActive: true }).subscribe({
            next: () => {
                this.toast.add({ severity: 'success', summary: 'Reactivated', detail: 'Activity type is back in the picker.' });
                this.load();
            },
            error: () => this.toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not reactivate the activity type.' })
        });
    }
}
